import React, { useEffect, useState } from 'react';
import { Wallet, Plus, Check, Trash2, Users as UsersIcon, Crown, RefreshCw, X } from 'lucide-react';
import { useData } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import { carsysApi } from '../api/carsysApi';
import { toast } from 'sonner';

import { useT } from '../context/LanguageContext';
import { confirmDialog } from '../lib/confirm';
const eur = (n) => (Number(n) || 0).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' });

export default function Verrekenen() {

  const { t } = useT();  const { bonnen } = useData();
  const { user } = useAuth();
  const [summary, setSummary] = useState({ totaleOmzet: 0, rows: [], totaalWinstAandeel: 0, totaleBetalingen: 0 });
  const [voorschotten, setVoorschotten] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [editShare, setEditShare] = useState(null); // {userId, naam, winstAandeel}
  const [statusFilter, setStatusFilter] = useState('open');

  const refresh = React.useCallback(() => {
    return Promise.all([carsysApi.getVerrekenenSummary(), carsysApi.getVoorschotten()])
      .then(([s, v]) => { setSummary(s); setVoorschotten(v); })
      .catch((e) => { toast.error(t('Kon verrekenen niet laden'), { description: e.response?.data?.detail || e.message }); });
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const isOwner = user?.role === 'owner';
  const filtered = voorschotten.filter(v => statusFilter === 'alle' || v.status === statusFilter);

  const handleMarkVerrekend = async (id) => {
    try { await carsysApi.markVoorschotVerrekend(id); toast.success(t('Verrekend')); refresh(); }
    catch (e) { toast.error(t('Mislukt'), { description: e.response?.data?.detail || e.message }); }
  };

  const handleDelete = async (id) => {
    if (!(await confirmDialog(t('Voorschot verwijderen?')))) return;
    try { await carsysApi.deleteVoorschot(id); toast.success(t('Verwijderd')); refresh(); }
    catch (e) { toast.error(t('Mislukt'), { description: e.response?.data?.detail || e.message }); }
  };

  const totaalAandeelOK = Math.abs(summary.totaalWinstAandeel - 100) < 0.01;

  return (
    <div data-testid="verrekenen-page">
      <div className="cs-page-header">
        <div><h2>{t('page.settle.title')}</h2><p>{t('page.settle.sub')}</p></div>
        <button data-testid="btn-refresh-verrekenen" className="cs-btn" onClick={refresh}><RefreshCw size={14} /> {t('Verversen', 'Verversen')}</button>
        <button data-testid="btn-new-voorschot" className="cs-btn primary" onClick={() => setShowNew(true)}><Plus size={14} /> {t('Nieuw voorschot', 'Nieuw voorschot')}</button>
      </div>

      <div className="cs-kpi-grid" style={{ marginBottom: 14 }}>
        <div className="cs-kpi" data-testid="kpi-totale-omzet"><Wallet size={20} className="icon" /><div className="label">{t('Totale omzet (lifetime)', 'Totale omzet (lifetime)')}</div><div className="value">{eur(summary.totaleOmzet)}</div></div>
        <div className="cs-kpi" data-testid="kpi-betalingen"><Wallet size={20} className="icon" /><div className="label">{t('Totaal ontvangen betalingen', 'Totaal ontvangen betalingen')}</div><div className="value">{eur(summary.totaleBetalingen)}</div></div>
        <div className="cs-kpi" data-testid="kpi-aandeel-check"><UsersIcon size={20} className="icon" />
          <div className="label">{t('Som winstaandelen', 'Som winstaandelen')}</div>
          <div className="value" style={{ color: totaalAandeelOK ? 'var(--cs-green)' : 'var(--cs-amber)' }}>{summary.totaalWinstAandeel?.toFixed(1)}%</div>
        </div>
      </div>

      {!totaalAandeelOK && summary.rows.length > 0 && (
        <div className="cs-card padded" style={{ background: 'var(--cs-amber-soft)', borderColor: 'var(--cs-amber)', marginBottom: 14, fontSize: 13 }}>
          ⚠️ {t('De som van alle winstaandelen is', 'De som van alle winstaandelen is')} <b>{summary.totaalWinstAandeel?.toFixed(1)}%</b> {t('en niet 100%. Pas de aandelen aan zodat ze samen 100% vormen.', 'en niet 100%. Pas de aandelen aan zodat ze samen 100% vormen.')}
        </div>
      )}

      <div className="cs-card padded" data-testid="card-team-share">
        <h3 className="cs-card-title"><Crown size={16} /> {t('Winstverdeling per teamlid', 'Winstverdeling per teamlid')}</h3>
        <table className="cs-table">
          <thead><tr><th>{t('Naam')}</th><th>{t('E-mail')}</th><th>{t('Rol')}</th><th style={{ textAlign: 'right' }}>{t('Winst%')}</th><th style={{ textAlign: 'right' }}>{t('Aandeel omzet')}</th><th style={{ textAlign: 'right' }}>{t('Voorgeschoten (open)')}</th><th style={{ textAlign: 'right' }}>{t('Saldo te ontvangen')}</th><th></th></tr></thead>
          <tbody>
            {summary.rows.map(r => (
              <tr key={r.userId} data-testid={`row-team-${r.userId}`}>
                <td style={{ fontWeight: 600 }}>{r.naam || '-'}</td>
                <td>{r.email}</td>
                <td><span className={`cs-badge ${r.role === 'owner' ? 'blue' : 'gray'}`}>{r.role === 'owner' ? t('Eigenaar', 'Eigenaar') : t('Medewerker', 'Medewerker')}</span></td>
                <td style={{ textAlign: 'right', fontWeight: 700 }}>{r.winstAandeel?.toFixed(1)}%</td>
                <td style={{ textAlign: 'right' }}>{eur(r.aandeelOmzet)}</td>
                <td style={{ textAlign: 'right', color: r.voorgeschoten_open > 0 ? 'var(--cs-amber)' : 'inherit' }}>{eur(r.voorgeschoten_open)}</td>
                <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--cs-green)' }}>{eur(r.saldo)}</td>
                <td>{isOwner && <button className="cs-btn ghost" data-testid={`btn-edit-share-${r.userId}`} onClick={() => setEditShare({ userId: r.userId, naam: r.naam, winstAandeel: r.winstAandeel })}>{t('Wijzig', 'Wijzig')}</button>}</td>
              </tr>
            ))}
            {summary.rows.length === 0 && <tr><td colSpan={8} className="cs-empty">{t('Geen teamleden.', 'Geen teamleden.')}</td></tr>}
          </tbody>
        </table>
        <div style={{ fontSize: 12, color: 'var(--cs-text-muted)', marginTop: 8 }}>
          <b>{t('Hoe werkt het?', 'Hoe werkt het?')}</b> {t('Het saldo = (winst% × totale omzet) + openstaande voorschotten. Als een voorschot verrekend wordt, gaat het van "open" naar "verrekend" en valt het uit het saldo.', 'Het saldo = (winst% × totale omzet) + openstaande voorschotten. Als een voorschot verrekend wordt, gaat het van "open" naar "verrekend" en valt het uit het saldo.')}
        </div>
      </div>

      <div className="cs-card padded" style={{ marginTop: 14 }} data-testid="card-voorschotten">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <h3 className="cs-card-title" style={{ margin: 0 }}><Wallet size={16} /> {t('Voorschotten', 'Voorschotten')} ({filtered.length})</h3>
          <div style={{ display: 'flex', gap: 4, background: '#fff', border: '1px solid var(--cs-border)', borderRadius: 6, padding: 2 }}>
            {[{ k: 'open', l: 'Open' }, { k: 'verrekend', l: 'Verrekend' }, { k: 'alle', l: 'Alle' }].map(s => (
              <button data-testid={`filter-${s.k}`} key={s.k} className="cs-btn ghost" style={{ padding: '4px 12px', background: statusFilter === s.k ? 'var(--cs-accent-soft)' : 'transparent', color: statusFilter === s.k ? 'var(--cs-accent)' : 'inherit' }} onClick={() => setStatusFilter(s.k)}>{t(s.l, s.l)}</button>
            ))}
          </div>
        </div>
        <table className="cs-table">
          <thead><tr><th>{t('Datum')}</th><th>{t('Persoon')}</th><th>{t('Omschrijving')}</th><th>{t('Gekoppelde bon')}</th><th style={{ textAlign: 'right' }}>{t('Bedrag')}</th><th>{t('Status')}</th><th></th></tr></thead>
          <tbody>
            {filtered.map(v => {
              const bon = v.bonId ? bonnen.find(b => b.id === v.bonId) : null;
              return (
                <tr key={v.id} data-testid={`row-voorschot-${v.id}`}>
                  <td>{v.datum}</td>
                  <td>{v.userNaam || v.userEmail}</td>
                  <td>{v.omschrijving || '-'}</td>
                  <td>{bon ? <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{bon.nummer}</span> : '-'}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{eur(v.bedrag)}</td>
                  <td><span className={`cs-badge ${v.status === 'verrekend' ? 'green' : 'amber'}`}>{v.status === 'verrekend' ? t('Verrekend') : t('Open')}</span></td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {v.status === 'open' && isOwner && <button data-testid={`btn-verrekenen-${v.id}`} className="cs-btn ghost" title={t('Markeer als verrekend', 'Markeer als verrekend')} onClick={() => handleMarkVerrekend(v.id)}><Check size={12} /></button>}
                    <button data-testid={`btn-delete-${v.id}`} className="cs-btn ghost danger" onClick={() => handleDelete(v.id)}><Trash2 size={12} /></button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan={7} className="cs-empty">{t('Geen voorschotten in deze categorie.', 'Geen voorschotten in deze categorie.')}</td></tr>}
          </tbody>
        </table>
      </div>

      {showNew && <NewVoorschotModal onClose={() => setShowNew(false)} onSaved={refresh} bonnen={bonnen} summary={summary} currentUserId={user.id} />}
      {editShare && <EditShareModal data={editShare} onClose={() => setEditShare(null)} onSaved={refresh} />}
    </div>
  );
}

function NewVoorschotModal({ onClose, onSaved, bonnen, summary, currentUserId }) {
  const { t } = useT();
  const [form, setForm] = useState({
    userId: currentUserId,
    datum: new Date().toISOString().slice(0, 10),
    bedrag: 0,
    omschrijving: '',
    bonId: '',
  });
  const save = async () => {
    if (form.bedrag <= 0) { toast.error(t('Vul een bedrag in')); return; }
    if (!form.omschrijving) { toast.error(t('Vul een omschrijving in')); return; }
    try {
      await carsysApi.createVoorschot({ ...form, bonId: form.bonId || undefined });
      toast.success(t('Voorschot toegevoegd'));
      onSaved(); onClose();
    } catch (e) {
      toast.error(t('Mislukt'), { description: e.response?.data?.detail || e.message });
    }
  };
  return (
    <div className="cs-modal-overlay" onClick={onClose}>
      <div className="cs-modal" onClick={e => e.stopPropagation()} data-testid="modal-new-voorschot">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>{t('Nieuw voorschot', 'Nieuw voorschot')}</h3>
          <button className="cs-btn ghost" onClick={onClose}><X size={14} /></button>
        </div>
        <div style={{ display: 'grid', gap: 10 }}>
          <div><label className="cs-section-title">{t('Wie heeft voorgeschoten?', 'Wie heeft voorgeschoten?')}</label>
            <select data-testid="select-user" className="cs-input" value={form.userId} onChange={e => setForm({ ...form, userId: e.target.value })}>
              {summary.rows.map(r => <option key={r.userId} value={r.userId}>{r.naam || r.email}</option>)}
            </select>
          </div>
          <div><label className="cs-section-title">{t('Datum')}</label><input type="date" className="cs-input" value={form.datum} onChange={e => setForm({ ...form, datum: e.target.value })} /></div>
          <div><label className="cs-section-title">{t('Bedrag (€)')}</label><input data-testid="input-bedrag" type="number" min="0" step="0.01" className="cs-input" value={form.bedrag} onChange={e => setForm({ ...form, bedrag: parseFloat(e.target.value) || 0 })} /></div>
          <div><label className="cs-section-title">{t('Omschrijving')}</label><input data-testid="input-omschrijving" className="cs-input" placeholder={t('bv. Onderdelen bij Kroymans gehaald', 'bv. Onderdelen bij Kroymans gehaald')} value={form.omschrijving} onChange={e => setForm({ ...form, omschrijving: e.target.value })} /></div>
          <div><label className="cs-section-title">{t('Gekoppelde bon', 'Gekoppelde bon')} ({t('optioneel', 'optioneel')})</label>
            <select className="cs-input" value={form.bonId} onChange={e => setForm({ ...form, bonId: e.target.value })}>
              <option value="">-- {t('geen', 'geen')} --</option>
              {bonnen.slice().sort((a, b) => (b.datum || '').localeCompare(a.datum || '')).slice(0, 50).map(b => <option key={b.id} value={b.id}>{b.nummer} - {b.kenteken || '-'}</option>)}
            </select>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <button className="cs-btn" onClick={onClose}>{t('Annuleren')}</button>
          <button data-testid="btn-save-voorschot" className="cs-btn primary" onClick={save}>{t('Opslaan')}</button>
        </div>
      </div>
    </div>
  );
}

function EditShareModal({ data, onClose, onSaved }) {
  const { t } = useT();
  const [pct, setPct] = useState(data.winstAandeel || 0);
  const save = async () => {
    try {
      await carsysApi.setTeamShare(data.userId, pct);
      toast.success(t('Winstaandeel opgeslagen'));
      onSaved(); onClose();
    } catch (e) {
      toast.error(t('Mislukt'), { description: e.response?.data?.detail || e.message });
    }
  };
  return (
    <div className="cs-modal-overlay" onClick={onClose}>
      <div className="cs-modal" onClick={e => e.stopPropagation()} data-testid="modal-edit-share">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>{t('Winstaandeel', 'Winstaandeel')} — {data.naam}</h3>
          <button className="cs-btn ghost" onClick={onClose}><X size={14} /></button>
        </div>
        <label className="cs-section-title">{t('Winstaandeel', 'Winstaandeel')} (%)</label>
        <input data-testid="input-share" type="number" min="0" max="100" step="0.1" className="cs-input" value={pct} onChange={e => setPct(parseFloat(e.target.value) || 0)} />
        <div style={{ fontSize: 11.5, color: 'var(--cs-text-muted)', marginTop: 6 }}>{t('De som van alle aandelen zou idealiter 100% moeten zijn.', 'De som van alle aandelen zou idealiter 100% moeten zijn.')}</div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <button className="cs-btn" onClick={onClose}>{t('Annuleren')}</button>
          <button data-testid="btn-save-share" className="cs-btn primary" onClick={save}>{t('Opslaan')}</button>
        </div>
      </div>
    </div>
  );
}
